// src/services/apiErrorHandler.ts
/**
 * Centralized API Error Handler
 * Detects and handles limit exceeded errors across all API services
 */

// Global limit exceeded callback - set by LimitExceededContext
let limitExceededCallback: ((message: string) => void) | null = null;

export const setLimitExceededCallback = (callback: (message: string) => void) => {
  limitExceededCallback = callback;
};

/**
 * Enhanced error class for API errors
 */
export class APIError extends Error {
  statusCode: number;
  isLimitExceeded: boolean;
  originalError?: any;

  constructor(message: string, statusCode: number, isLimitExceeded: boolean = false) {
    super(message);
    this.name = 'APIError';
    this.statusCode = statusCode;
    this.isLimitExceeded = isLimitExceeded;
  }
}

/**
 * Parse and handle API error responses
 * Detects limit exceeded errors and triggers modal
 */
export const handleAPIError = async (response: Response): Promise<never> => {
  let errorData: any;
  
  try {
    errorData = await response.json();
  } catch (e) {
    // If parsing fails, throw generic error
    throw new APIError(
      `HTTP error! status: ${response.status}`,
      response.status
    );
  }

  // Check for 403 limit exceeded error
  if (response.status === 403 && errorData?.message) {
    const message = errorData.message.toLowerCase();
    
    // Check if message contains limit exceeded keywords
    if (message.includes('limit exceeded') || 
        message.includes('image_generation') ||
        message.includes('video_generation') ||
        message.includes('audio_generation') ||
        message.includes('script_generation') ||
        message.includes('prompt_generation') ||
        message.includes('caption_generation') ||
        message.includes('music_generation') ||
        message.includes('narration') ||
        message.includes('quota') ||
        message.includes('rate limit')) {
      
      // Trigger the global limit modal if callback is set
      if (limitExceededCallback) {
        limitExceededCallback(errorData.message);
      }
      
      // Still throw error so component can handle it if needed
      throw new APIError(errorData.message, 403, true);
    }
  }

  // For other errors, throw with message if available
  throw new APIError(
    errorData?.message || `HTTP error! status: ${response.status}`,
    response.status
  );
};

/**
 * Wrapper for fetch calls with automatic error handling
 * Usage: const data = await fetchWithErrorHandling(url, options);
 */
export const fetchWithErrorHandling = async (
  url: string,
  options?: RequestInit
): Promise<any> => {
  const response = await fetch(url, options);

  if (!response.ok) {
    await handleAPIError(response);
  }

  return response.json();
};

/**
 * Check if error is a limit exceeded error
 */
export const isLimitExceededError = (error: any): boolean => {
  return error instanceof APIError && error.isLimitExceeded;
};
